import { createFileRoute } from "@tanstack/react-router";
import { UserSettingsPage, type UserTab } from "@/components/admin/user-settings-page";
import { userAnalyticsQuery } from "@/hooks/use-user-analytics";
import { requireAdminSection } from "@/lib/admin-guard";
import { ANALYTICS_RANGES, type AnalyticsRange } from "@/lib/user-analytics-api";

export const Route = createFileRoute("/_admin/users/$id")({
  // Both optional so existing `/users/$id` links keep working; the page
  // defaults to the Overview over the last 30 days.
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === "account" ? ("account" as UserTab) : undefined,
    range: ANALYTICS_RANGES.includes(search.range as AnalyticsRange)
      ? (search.range as AnalyticsRange)
      : undefined,
  }),
  beforeLoad: ({ context }) => requireAdminSection(context.auth.capabilities, "users"),
  loaderDeps: ({ search }) => ({ tab: search.tab, range: search.range }),
  // Warm the Overview without blocking navigation on its aggregates.
  loader: ({ context, params, deps }) => {
    if (deps.tab !== "account") {
      void context.queryClient.prefetchQuery(userAnalyticsQuery(params.id, deps.range ?? "30d"));
    }
  },
  component: AdminUserSettingsRoute,
});

function AdminUserSettingsRoute() {
  const { id } = Route.useParams();
  const { tab = "overview", range = "30d" } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <UserSettingsPage
      userId={id}
      tab={tab}
      range={range}
      onTabChange={(next) =>
        void navigate({
          search: (prev) => ({ ...prev, tab: next === "overview" ? undefined : next }),
          replace: true,
        })
      }
      onRangeChange={(next) =>
        void navigate({
          search: (prev) => ({ ...prev, range: next === "30d" ? undefined : next }),
          replace: true,
        })
      }
    />
  );
}
